import { z } from 'zod';

// Request schemas are camelCase. The api client snake_cases the body after validation.

// ==================== AG Grid SSRM Column ====================

export const ColumnSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  field: z.string(),
  aggFunc: z.string().optional(),
  bucketing: z.enum(['day', 'week', 'month', 'quarter', 'year']).optional(),
});

// ==================== Sort Model ====================

export const SortModelItemSchema = z.object({
  colId: z.string(),
  sort: z.enum(['asc', 'desc']),
});

// ==================== Filter Models ====================

export const TextFilterSchema = z.object({
  filterType: z.literal('text'),
  type: z.enum([
    'contains',
    'notContains',
    'equals',
    'notEqual',
    'startsWith',
    'endsWith',
    'blank',
    'notBlank',
  ]),
  filter: z.string().optional(),
});

export const DateFilterSchema = z.object({
  filterType: z.literal('date'),
  type: z.enum([
    'equals',
    'notEqual',
    'lessThan',
    'greaterThan',
    'lessThanOrEqual',
    'greaterThanOrEqual',
    'inRange',
    'blank',
    'notBlank',
    'yesterday',
    'lastWeek',
    'lastMonth',
    'thisMonth',
    'lastQuarter',
    'thisQuarter',
    'lastYear',
    'thisYear',
    'last12Months',
    'allTime',
  ]),
  dateFrom: z.string().nullable().optional(),
  dateTo: z.string().nullable().optional(),
});

export const NumberFilterSchema = z.object({
  filterType: z.literal('number'),
  type: z.enum([
    'equals',
    'notEqual',
    'greaterThan',
    'greaterThanOrEqual',
    'lessThan',
    'lessThanOrEqual',
    'inRange',
    'blank',
    'notBlank',
  ]),
  // It should be number, but our backend working with string values for number filters, so we need to support both types here
  filter: z.union([z.number(), z.string()]).optional(),
  filterTo: z.union([z.number(), z.string()]).nullable().optional(),
});

export const CombinedFilterSchema = z.object({
  filterType: z.enum(['text', 'number', 'date']),
  operator: z.enum(['AND', 'OR']),
  conditions: z.array(
    z.union([TextFilterSchema, NumberFilterSchema, DateFilterSchema]),
  ),
});

export const SetFilterSchema = z.object({
  filterType: z.literal('set'),
  values: z.array(z.string()),
});

export const FilterModelValueSchema = z.union([
  TextFilterSchema,
  DateFilterSchema,
  NumberFilterSchema,
  CombinedFilterSchema,
  SetFilterSchema,
]);

export const FilterModelSchema = z.record(z.string(), FilterModelValueSchema);

// ==================== Request ====================

export const GridDataRequestSchema = z.object({
  startRow: z.number(),
  endRow: z.number(),
  rowGroupCols: z.array(ColumnSchema),
  valueCols: z.array(ColumnSchema),
  pivotCols: z.array(ColumnSchema).optional().default([]),
  pivotMode: z.boolean().optional().default(false),
  groupKeys: z.array(z.string().nullable()),
  sortModel: z.array(SortModelItemSchema).optional().default([]),
  filterModel: FilterModelSchema.optional().default({}),
  needsGrandTotal: z.boolean().optional(),
});

// ==================== Response ====================

export const GridDataResponseSchema = z
  .object({
    row_data: z.array(z.record(z.string(), z.unknown())),
    row_count: z.number(),
    pivot_result_fields: z.array(z.string()).optional(),
    grand_total_data: z.record(z.string(), z.unknown()).nullable().optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
  })
  .catchall(z.unknown());

type GridDataResponse = z.infer<typeof GridDataResponseSchema>;

type GridRow = Record<string, unknown>;

export type GridData = {
  rowData: GridRow[];
  rowCount: number;
  pivotResultFields?: string[];
  grandTotalData?: GridRow | null;
  meta?: GridRow;
};

/**
 * Reserved response field names. A snake_case name is rewritten; the others
 * already match the app. Anything else, including column ids, is copied as sent.
 */
const GRID_FIELD_KEYS = {
  id: 'id',
  label: 'label',
  value: 'value',
  values: 'values',
  format: 'format',
  value_id: 'valueId',
  value_type: 'valueType',
  dimension_type: 'dimensionType',
  metric_type: 'metricType',
  pivot_separator: 'pivotSeparator',
} as const;

type GridFieldKey = keyof typeof GRID_FIELD_KEYS;

const isGridFieldKey = (key: string): key is GridFieldKey =>
  Object.hasOwn(GRID_FIELD_KEYS, key);

const renameGridFieldKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(renameGridFieldKeys);
  }

  if (typeof value !== 'object' || value === null) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nested]) => [
      isGridFieldKey(key) ? GRID_FIELD_KEYS[key] : key,
      renameGridFieldKeys(nested),
    ]),
  );
};

/** Top-level keys stay as sent. Only nested field names from the dictionary are renamed. */
const toGridRow = (row: GridRow): GridRow =>
  Object.fromEntries(
    Object.entries(row).map(([columnId, cell]) => [
      columnId,
      renameGridFieldKeys(cell),
    ]),
  );

export const toGridData = (response: GridDataResponse): GridData => ({
  rowData: response.row_data.map(toGridRow),
  rowCount: response.row_count,
  pivotResultFields: response.pivot_result_fields,
  grandTotalData:
    response.grand_total_data === null ||
    response.grand_total_data === undefined
      ? response.grand_total_data
      : toGridRow(response.grand_total_data),
  meta: response.meta ? toGridRow(response.meta) : undefined,
});
